import { useEffect, useState, useCallback } from 'react';
import { urgentTasksAPI, agentAPI } from '@/lib/api';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Trash2, CheckCircle2, Clock } from 'lucide-react';

const EMPTY = {
  title: '', description: '', task_type: 'Complaint', priority: 'High',
  assigned_agent_npn: '', deadline: '', link: '', language: 'en',
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function smsReason(code) {
  return {
    no_phone: "the agent has no mobile number on file",
    no_consent: "the agent hasn't opted in to Agent SMS yet",
    opted_out: "the agent opted out of texts",
    phone_changed: "the agent's number changed — they must re-opt in",
    channel_not_configured: "the Agent SMS channel isn't configured on the server",
    no_agent: "the agent account couldn't be located",
    error: "an unexpected error occurred",
  }[code] || (code || 'it was skipped');
}

export default function UrgentTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [meta, setMeta] = useState({ task_types: [], priorities: [] });
  const [form, setForm] = useState(EMPTY);
  const [statusFilter, setStatusFilter] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await urgentTasksAPI.list(statusFilter ? { status: statusFilter } : {});
      setTasks(data?.items || []);
    } catch { toast.error('Failed to load tasks'); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    urgentTasksAPI.meta().then(({ data }) => setMeta(data)).catch(() => {});
    agentAPI.list?.().then(({ data }) => setAgents(data?.items || data?.agents || data || [])).catch(() => {});
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.assigned_agent_npn || !form.deadline) {
      toast.error('Title, agent and deadline are required'); return;
    }
    setSaving(true);
    try {
      const { data } = await urgentTasksAPI.create({ ...form, deadline: new Date(form.deadline).toISOString() });
      const n = data?.notifications || {};
      const delivered = [];
      if (n.in_app) delivered.push('in-app');
      if (n.email?.sent) delivered.push('email');
      if (n.sms?.sent) delivered.push('SMS');
      toast.success(
        delivered.length
          ? `Urgent task assigned — agent notified via ${delivered.join(', ')}`
          : 'Urgent task assigned'
      );
      if (n.sms && !n.sms.sent) {
        toast.warning(`SMS not sent: ${smsReason(n.sms.reason)}`, { duration: 7000 });
      }
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create task');
    } finally { setSaving(false); }
  };

  const complete = async (id) => {
    try { await urgentTasksAPI.complete(id); toast.success('Completed'); load(); }
    catch { toast.error('Failed'); }
  };
  const remove = async (id) => {
    try { await urgentTasksAPI.remove(id); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="urgent-tasks-page">
      <div className="flex items-center gap-2">
        <AlertTriangle className="text-red-600" />
        <h1 className="text-2xl font-bold text-slate-900">Urgent Tasks</h1>
      </div>

      <form onSubmit={submit} className="bg-white rounded-xl border p-4 grid grid-cols-1 md:grid-cols-2 gap-3"
            data-testid="urgent-task-form">
        <input data-testid="ut-title" className="border rounded-md px-3 py-2 md:col-span-2" placeholder="Title *"
               value={form.title} onChange={(e) => upd('title', e.target.value)} />
        <textarea data-testid="ut-description" className="border rounded-md px-3 py-2 md:col-span-2" placeholder="Description"
                  value={form.description} onChange={(e) => upd('description', e.target.value)} />
        <select data-testid="ut-type" className="border rounded-md px-3 py-2" value={form.task_type}
                onChange={(e) => upd('task_type', e.target.value)}>
          {(meta.task_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="ut-priority" className="border rounded-md px-3 py-2" value={form.priority}
                onChange={(e) => upd('priority', e.target.value)}>
          {(meta.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="ut-agent" className="border rounded-md px-3 py-2" value={form.assigned_agent_npn}
                onChange={(e) => upd('assigned_agent_npn', e.target.value)}>
          <option value="">— Assign to agent * —</option>
          {agents.map((a) => (
            <option key={a.npn || a.id} value={a.npn}>
              {(a.first_name || '') + ' ' + (a.last_name || '')} ({a.npn})
            </option>
          ))}
        </select>
        <select data-testid="ut-language" className="border rounded-md px-3 py-2" value={form.language}
                onChange={(e) => upd('language', e.target.value)}>
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
        <input data-testid="ut-deadline" type="datetime-local" className="border rounded-md px-3 py-2"
               value={form.deadline} onChange={(e) => upd('deadline', e.target.value)} />
        <input data-testid="ut-link" className="border rounded-md px-3 py-2" placeholder="Link (optional)"
               value={form.link} onChange={(e) => upd('link', e.target.value)} />
        <button data-testid="ut-submit" disabled={saving}
                className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-[#0f2547] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#13315f] disabled:opacity-60">
          <Plus size={16} /> Assign urgent task
        </button>
      </form>

      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">Filter:</span>
        {['', 'open', 'overdue', 'completed'].map((s) => (
          <button key={s || 'all'} data-testid={`ut-filter-${s || 'all'}`}
                  onClick={() => setStatusFilter(s)}
                  className={`text-xs px-3 py-1 rounded-full border ${statusFilter === s ? 'bg-[#0f2547] text-white' : 'bg-white'}`}>
            {s || 'all'}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="urgent-tasks-list">
        {tasks.length === 0 && <p className="text-slate-500 text-sm">No tasks.</p>}
        {tasks.map((t) => (
          <div key={t.task_id} data-testid={`ut-row-${t.task_id}`}
               className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
                 t.status === 'completed' ? 'bg-slate-50 opacity-70'
                   : t.is_overdue ? 'border-red-300 bg-red-50' : 'bg-white'}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                  t.status === 'completed' ? 'bg-green-600 text-white'
                    : t.is_overdue ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                  {t.status === 'completed' ? 'Done' : t.is_overdue ? 'Overdue' : t.priority}
                </span>
                <span className="text-xs text-slate-500">{t.task_type} · {t.assigned_agent_name}</span>
              </div>
              <p className="font-semibold text-slate-900 truncate">{t.title}</p>
              <p className="flex items-center gap-1 text-xs text-slate-500"><Clock size={12} /> Due {fmt(t.deadline)}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {t.status !== 'completed' && (
                <button data-testid={`ut-complete-${t.task_id}`} onClick={() => complete(t.task_id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 border border-green-300 rounded px-2 py-1 hover:bg-green-50">
                  <CheckCircle2 size={14} /> Complete
                </button>
              )}
              <button data-testid={`ut-remove-${t.task_id}`} onClick={() => remove(t.task_id)}
                      className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
